import { useCallback, useEffect, useState, Fragment } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ScadenzaEditor, RinnovaDialog } from "@/components/ScadenzaForms";
import { statusMeta, giorniLabel } from "@/lib/scadenze";
import { formatDate, formatCurrency } from "@/lib/format";
import { Plus, Pencil, Trash2, RefreshCw, History, Loader2, ExternalLink } from "lucide-react";

const WRITE = {
  veicolo: ["admin", "traffico", "officina"],
  autista: ["admin", "traffico"],
};

export default function ScadenzeDialog({ open, onOpenChange, entityType, record }) {
  const { user } = useAuth();
  const canWrite = user?.role === "admin" || WRITE[entityType]?.includes(user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [rinnova, setRinnova] = useState(null);
  const [storico, setStorico] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    api
      .get(`/scadenze`, { params: { entity_type: entityType, entity_id: record.id } })
      .then((r) => setItems(r.data.items))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [entityType, record.id]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const del = async (id) => {
    try {
      await api.delete(`/scadenze/${id}`);
      toast.success("Scadenza eliminata");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const loadStorico = async (id) => {
    if (storico[id]) {
      setStorico((p) => ({ ...p, [id]: null }));
      return;
    }
    const r = await api.get(`/scadenze/${id}/storico`);
    setStorico((p) => ({ ...p, [id]: r.data }));
  };

  const title =
    entityType === "veicolo"
      ? `Scadenze veicolo · ${record.targa}`
      : `Scadenze autista · ${record.cognome} ${record.nome}`;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="scadenze-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">{title}</DialogTitle>
            <DialogDescription>
              Documenti e scadenze con stato colorato. Il rinnovo archivia la versione
              precedente nello storico.
            </DialogDescription>
          </DialogHeader>

          {canWrite && (
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={() => {
                  setEditing(null);
                  setEditorOpen(true);
                }}
                data-testid="scadenza-new"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                Nuova scadenza
              </Button>
            </div>
          )}

          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="text-left px-3 py-2">Tipo</th>
                  <th className="text-left px-3 py-2">Scadenza</th>
                  <th className="text-left px-3 py-2">Stato</th>
                  <th className="text-right px-3 py-2">Ultimo costo</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="py-10 text-center text-slate-400">
                      <Loader2 className="h-5 w-5 animate-spin inline" />
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-10 text-center text-slate-400 text-sm">
                      Nessuna scadenza registrata
                    </td>
                  </tr>
                ) : (
                  items.map((s) => {
                    const sm = statusMeta(s.status);
                    const hist = storico[s.id];
                    return (
                      <Fragment key={s.id}>
                        <tr className={`border-t border-slate-100 ${sm.row}`} data-testid={`scadenza-row-${s.id}`}>
                          <td className="px-3 py-2">
                            <div className="font-medium text-slate-800">{s.tipo}</div>
                            {s.descrizione && (
                              <div className="text-xs text-slate-500">{s.descrizione}</div>
                            )}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {formatDate(s.data_scadenza)}
                            <div className="text-xs text-slate-400">{giorniLabel(s.giorni)}</div>
                          </td>
                          <td className="px-3 py-2">
                            <span className={`scad-badge ${sm.cls}`}>{sm.label}</span>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {s.costo_ultimo_rinnovo != null
                              ? formatCurrency(s.costo_ultimo_rinnovo)
                              : "—"}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center justify-end gap-1">
                              {s.documento_url && (
                                <a href={s.documento_url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-sky-700 p-1">
                                  <ExternalLink className="h-4 w-4" />
                                </a>
                              )}
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-400 hover:text-slate-700" onClick={() => loadStorico(s.id)} title="Storico" data-testid={`scadenza-storico-${s.id}`}>
                                <History className="h-4 w-4" />
                              </Button>
                              {canWrite && (
                                <>
                                  <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-400 hover:text-green-700" onClick={() => setRinnova(s)} title="Rinnova" data-testid={`scadenza-rinnova-${s.id}`}>
                                    <RefreshCw className="h-4 w-4" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-400 hover:text-sky-700" onClick={() => { setEditing(s); setEditorOpen(true); }} data-testid={`scadenza-edit-${s.id}`}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-400 hover:text-red-600" onClick={() => del(s.id)} data-testid={`scadenza-del-${s.id}`}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                        {hist && (
                          <tr key={s.id + "-h"} className="bg-slate-50">
                            <td colSpan={5} className="px-3 py-2">
                              <div className="text-xs font-semibold text-slate-500 mb-1">
                                Storico rinnovi
                              </div>
                              {hist.length === 0 ? (
                                <div className="text-xs text-slate-400">Nessun rinnovo precedente</div>
                              ) : (
                                <ul className="text-xs text-slate-600 space-y-0.5">
                                  {hist.map((h) => (
                                    <li key={h.archived_id}>
                                      Scadeva il {formatDate(h.data_scadenza)}
                                      {h.costo_ultimo_rinnovo != null &&
                                        ` · costo ${formatCurrency(h.costo_ultimo_rinnovo)}`}
                                      {" · archiviato "}
                                      {formatDate(h.archived_at)}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      <ScadenzaEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        entityType={entityType}
        entityId={record.id}
        initial={editing}
        onSaved={load}
      />
      {rinnova && (
        <RinnovaDialog
          open={!!rinnova}
          onOpenChange={(v) => !v && setRinnova(null)}
          scadenza={rinnova}
          onSaved={load}
        />
      )}
    </>
  );
}
